import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Tag } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';

const Blog = () => {
  return (
    <div className="page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            <div className="badge">Insights & Analysis</div>
            <h1 className="heading-1">Full-Bin UK Energy Blog</h1>
            <p className="body-large">
              Technical case studies, energy efficiency insights, and industry analysis from our engineering team. Evidence-based content for facilities managers and sustainability professionals.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {blogPosts.filter(post => post.featured).map(post => (
        <section key={post.id} className="section" style={{background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f5e9 100%)'}}>
          <div className="container">
            <div style={{maxWidth: '1000px', margin: '0 auto'}}>
              <div style={{marginBottom: '15px'}}>
                <span style={{
                  background: '#ffa726',
                  color: 'white',
                  padding: '6px 16px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Featured Case Study</span>
              </div>
              <h2 style={{fontSize: '38px', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a', lineHeight: '1.3'}}>
                {post.title}
              </h2>
              <div style={{display: 'flex', gap: '25px', marginBottom: '20px', flexWrap: 'wrap', color: '#555', fontSize: '14px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <Calendar size={16} />
                  <span>{new Date(post.publishDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <Clock size={16} />
                  <span>{post.readTime}</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <Tag size={16} />
                  <span>{post.category}</span>
                </div>
              </div>
              <p style={{fontSize: '18px', color: '#333', marginBottom: '30px', lineHeight: '1.7'}}>
                {post.excerpt}
              </p>
              <Link 
                to={`/blog/${post.slug}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 32px',
                  background: '#00897b',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
              >
                Read Full Technical Analysis
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      ))}

      {/* All Posts Grid */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">All Articles</h2>
            <p className="section-subtitle">
              Technical insights, case studies, and industry analysis
            </p>
          </div>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '30px', maxWidth: '1200px', margin: '0 auto'}}>
            {blogPosts.map(post => (
              <article key={post.id} style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{marginBottom: '12px'}}>
                  <span style={{
                    background: '#e8f5e9',
                    color: '#00897b',
                    padding: '5px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>{post.category}</span>
                </div>
                
                <h3 style={{fontSize: '22px', fontWeight: '700', marginBottom: '12px', color: '#1a1a1a', lineHeight: '1.4'}}>
                  {post.title}
                </h3>
                
                <div style={{display: 'flex', gap: '15px', marginBottom: '15px', color: '#666', fontSize: '13px'}}>
                  <span style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                    <Calendar size={14} />
                    {new Date(post.publishDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  <span style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                    <Clock size={14} />
                    {post.readTime}
                  </span>
                </div>
                
                <p style={{fontSize: '15px', color: '#555', marginBottom: '20px', lineHeight: '1.6', flexGrow: 1}}>
                  {post.excerpt}
                </p>
                
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px'}}>
                  {post.tags.slice(0, 3).map(tag => (
                    <span key={tag} style={{
                      background: '#f5f5f5',
                      color: '#666',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
                
                <Link 
                  to={`/blog/${post.slug}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#00897b',
                    fontSize: '15px',
                    fontWeight: '600',
                    textDecoration: 'none'
                  }}
                >
                  Read Article
                  <ArrowRight size={16} />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-2">Want similar results for your facility?</h2>
            <p className="body-large">
              Our engineering team can assess your energy systems and identify opportunities for measurable savings.
            </p>
            <Link to="/contact" className="btn-primary">
              Book Free Energy Audit
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
